/**
 * ============================================================================
 * NewbannerFormTemplete — Controller (client)
 * ============================================================================
 * Owns thank-you / redirect shell state and Open RFI click analytics.
 * No JSX — view consumes ViewModel. Form logic stays in
 * useProgramDetailsOpenRfiController via ProgramDetailsOpenRfi.
 * Behavior preserved from pre-MVC wrapper.
 * ============================================================================
 */
"use client";

import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue, updateStateSession } from "@/context/utils";
import type {
  NewbannerFormTempleteProps,
  NewbannerFormTempleteViewModel,
} from "../model/newbannerFormTemplete.types";

export function useNewbannerFormTempleteController(
  props: NewbannerFormTempleteProps,
): NewbannerFormTempleteViewModel {
  const {
    isMobile = false,
    isFaq = false,
    associatedPrograms,
    ProgramId,
    FaqHeading = "",
    RFIHeading = "",
    sectionName = "hero_banner",
    BannerImageTemplete = {},
    isLandingPage = false,
    isLambdaAPI = false,
    isAffiliatePage = false,
    seatFilledDisplay = true,
    isStatsDisplay = true,
    isJobRole = false,
    seatFilledCount,
    isJobPage = false,
    isOneYearMba = false,
    className,
    ctaButtonLabel,
    isCityPage = false,
    redirectToThankYou = false,
    slug = ""
  } = props;

  const [isApplicationRedirectLoaderDisabled, setApplicationRedirectLoaderDisabled] =
    useState(true);
  const [loadingCount, setLoadingCount] = useState(3);
  const ref = useRef<HTMLDivElement | null>(null);
  const isClickedOnce = useRef(false);
  const counterInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  const redirectHandler = useCallback((link?: string) => {
    if (!link) return;
    window.location.href = link + "&e=1";
  }, []);

  useEffect(() => {
    if (!redirectToThankYou || isApplicationRedirectLoaderDisabled) return;

    setLoadingCount(3);
    counterInterval.current = setInterval(() => {
      setLoadingCount((count) => {
        if (count <= 1) {
          if (counterInterval.current) clearInterval(counterInterval.current);
          window.location.href = "/thankyou";
          return 0;
        }
        return count - 1;
      });
    }, 1000);

    return () => {
      if (counterInterval.current) clearInterval(counterInterval.current);
    };
  }, [redirectToThankYou, isApplicationRedirectLoaderDisabled]);

  const gtEventHandler = useCallback(
    (e: MouseEvent) => {
      // Submit clicks must not fire lead_initiate (invalid submits especially).
      const target = e.target as HTMLElement | null;
      if (target?.closest?.('button#submit-button, button[type="submit"]')) {
        return;
      }

      if (!isClickedOnce.current) {
        pushToDataLayer({
          event: "lead_initiate",
          cta_text: "Open RFI",
          section_name: sectionName,
          page_type: getGlobalStateValue("pageType"),
          page_url: window.location.href,
        });

        updateStateSession({
          parentSection: sectionName,
          leadStartingCta: "Open RFI",
        });

        window?.webengage?.track("Open RFI Clicked", {
          "Page Type": getGlobalStateValue("pageType"),
          "Section Name": isFaq ? "Footer" : "Hero Banner",
        });

        isClickedOnce.current = true;
      }
    },
    [isFaq, sectionName],
  );

  return {
    ref,
    isMobile,
    isFaq,
    associatedPrograms,
    ProgramId,
    FaqHeading,
    RFIHeading,
    BannerImageTemplete,
    isLandingPage,
    isLambdaAPI,
    isAffiliatePage,
    seatFilledDisplay,
    isStatsDisplay,
    isJobRole,
    seatFilledCount,
    isJobPage,
    isOneYearMba,
    className,
    ctaButtonLabel,
    isCityPage,
    redirectToThankYou,
    loadingCount,
    isApplicationRedirectLoaderDisabled,
    slug,
    setApplicationRedirectLoaderDisabled,
    gtEventHandler,
    redirectHandler,
  };
}
